import React, { useEffect, useRef, useState } from 'react';
import { Mic, Square, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from '@/hooks/use-toast';
import { emrService } from '@/services/emrService';
import { useEncounter } from '@/contexts/EncounterContext';
import { parseApiError } from '@/lib/apiErrors';
import { cn } from '@/lib/utils';

type State = 'idle' | 'starting' | 'recording' | 'stopping';

const formatTimer = (s: number) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
};

/**
 * Ambient capture control — header button that starts/stops a recording for
 * the current encounter. Backend is currently a stub: the button works
 * end-to-end but the AI pipeline (transcription + SOAP) is wired up
 * separately. The control is intentionally visible so practitioners can
 * validate the affordance ahead of the AI work.
 */
const AmbientRecordControl: React.FC = () => {
  const { clinicId, encounterId, isSigned } = useEncounter();
  const [state, setState] = useState<State>('idle');
  const [seconds, setSeconds] = useState(0);
  const [transcriptId, setTranscriptId] = useState<string | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (state !== 'recording') {
      if (tickRef.current) clearInterval(tickRef.current);
      tickRef.current = null;
      return;
    }
    tickRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [state]);

  const start = async () => {
    setState('starting');
    setSeconds(0);
    try {
      const result = await emrService.startEncounterTranscript(clinicId, encounterId);
      setTranscriptId(result.transcriptId);
      setState('recording');
      if (result.stub) {
        toast({
          title: 'Recording started',
          description: 'Ambient AI is in progress — your audio is captured for testing only.',
          variant: 'default',
        });
      }
    } catch (err) {
      setState('idle');
      const e = parseApiError(err);
      toast({ title: 'Could not start recording', description: e.message, variant: 'destructive' });
    }
  };

  const stop = async () => {
    setState('stopping');
    const captured = seconds;
    try {
      const result = await emrService.stopEncounterTranscript(clinicId, encounterId, {
        transcriptId: transcriptId || undefined,
        durationSeconds: captured,
      });
      toast({
        title: 'Recording stopped',
        description: result.soapGenerationStatus === 'not_implemented'
          ? `Captured ${formatTimer(captured)}. SOAP generation will run once the ambient pipeline is enabled.`
          : `Captured ${formatTimer(captured)}.`,
        variant: 'default',
      });
    } catch (err) {
      const e = parseApiError(err);
      toast({ title: 'Stop failed', description: e.message, variant: 'destructive' });
    } finally {
      setTranscriptId(null);
      setSeconds(0);
      setState('idle');
    }
  };

  if (isSigned) return null;

  if (state === 'recording' || state === 'stopping') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant='outline'
            size='sm'
            onClick={stop}
            disabled={state === 'stopping'}
            className={cn(
              'gap-1 border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100',
              state === 'recording' && 'animate-pulse',
            )}
          >
            {state === 'stopping'
              ? <Loader2 className='h-3.5 w-3.5 animate-spin' />
              : <Square className='h-3.5 w-3.5 fill-current' />}
            <span className='font-medium'>Stop · {formatTimer(seconds)}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>Stop ambient capture</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant='outline'
          size='sm'
          onClick={start}
          disabled={state === 'starting'}
          className='gap-1 text-slate-600'
        >
          {state === 'starting'
            ? <Loader2 className='h-3.5 w-3.5 animate-spin' />
            : <Mic className='h-3.5 w-3.5' />}
          <span className='hidden sm:inline'>Record</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent className='max-w-[260px]'>
        <p className='font-medium'>Ambient capture (preview)</p>
        <p className='mt-1 flex items-start gap-1 text-xs text-slate-500'>
          <AlertCircle className='mt-0.5 h-3 w-3 shrink-0' />
          Backend pipeline is being wired up — your audio is captured but SOAP
          generation runs once the AI is enabled.
        </p>
      </TooltipContent>
    </Tooltip>
  );
};

export default AmbientRecordControl;
